import React, { useEffect, useState } from 'react';
import useGenversePage from '../../../../hooks/useGenversePage';
import PageNFTGrid from '../../../../components/PageNFTGrid';
import {
    Accordion,
    AccordionButton,
    AccordionIcon,
    AccordionItem,
    AccordionPanel,
    Box,
    Flex,
    FormControl,
    FormLabel,
    Heading,
    Image,
    Select,
    Text,
    useColorModeValue,
} from '@chakra-ui/react';
import {
    GenverseTokenType,
    SearchWalletResultsDataType,
} from '../../../../../../types';
import { useLocation, useNavigate } from '@reach/router';
import queryString from 'query-string';
import { isEmpty, parseInt } from 'lodash';
import SearchWalletTokensInput from '../../../base/SearchWalletTokensInput';
import G3LogoSpinner from '../../../general/G3LogoSpinner';
import getTokenMetadataImage from '../../../../utils/getTokenMetadataImage';

export const BY_NFT = 'BY_NFT';
export const BY_COLLECTION = 'BY_COLLECTION';

export default ({ walletAddress }: { walletAddress: string }) => {
    const getQS = () => queryString?.parse(location?.search);
    const getQSGroupBy = () => getQS()?.groupBy;
    const getQSExpandedIndex = () =>
        parseInt((getQS()?.expIndex || null) as string) as number;
    const [groupBy, setGroupBy] = useState(getQSGroupBy());
    const [expandedIndex, setExpandedIndex] = useState(
        getQSExpandedIndex() as number
    );

    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        // Maintain local state with query parameters
        if (groupBy !== getQSGroupBy()) setGroupBy(getQSGroupBy());
        if (expandedIndex !== getQSExpandedIndex())
            setExpandedIndex(getQSExpandedIndex());
    }, [groupBy, expandedIndex, location?.search]);

    // @ts-ignore
    const { genversePage, hasNextPage, loadNextPage, loading, isCurrentUser } =
        useGenversePage({
            address: walletAddress,
            // @ts-ignore
            groupBy,
        });

    const updateQueryParams = (params: any) => {
        const queryParams = {
            ...getQS(), // current params maintain
            ...(params ? { ...params } : {}),
        };
        const newPath = `${location.pathname}${
            !isEmpty(queryParams)
                ? `?${queryString.stringify(queryParams)}`
                : ''
        }`;

        if (location?.pathname !== newPath) navigate(newPath);
    };

    const onChangeGroupBy = (newGroupBy: string) =>
        updateQueryParams({ groupBy: newGroupBy });
    const onChangeExpandedIndex = (expIndex: number) =>
        updateQueryParams({ expIndex });

    useEffect(() => {
        setGroupBy(BY_NFT);
    }, [walletAddress]);

    const [searchResults, setSearchResults] = useState(
        {} as SearchWalletResultsDataType
    );

    const onSearchResults = (
        searchResultsData: SearchWalletResultsDataType
    ) => {
        setSearchResults(searchResultsData);
    };

    const isLoading = loading || searchResults?.loading;

    return (
        <>
            <Flex
                direction={['column-reverse', 'row']}
                align="center"
                justify="space-between"
                padding={['0 1rem', 0]}
            >
                <Flex marginTop={['2rem', 0]}>
                    {(!groupBy || groupBy === BY_NFT) && (
                        <SearchWalletTokensInput
                            key={walletAddress}
                            useURLQueryParams
                            walletAddress={walletAddress}
                            onSearchResults={onSearchResults}
                        />
                    )}
                    {groupBy === BY_COLLECTION &&
                        !isLoading &&
                        /*@ts-ignore*/
                        genversePage?.collections?.length >= 0 && (
                            <Heading
                                size="lg"
                                paddingLeft={[0, '1rem']}
                            >{`${genversePage?.collections?.length} Collections`}</Heading>
                        )}
                </Flex>
                <FormControl maxWidth={['40%', '10rem']}>
                    <FormLabel htmlFor="token-group-select" fontSize=".8rem">
                        View By
                    </FormLabel>
                    <Select
                        id="token-group-select"
                        variant="outline"
                        size="sm"
                        value={groupBy || undefined}
                        onChange={(e) => onChangeGroupBy(e.target.value)}
                        borderColor={useColorModeValue(
                            'rgba(0, 0, 0, 0.1)',
                            'rgba(255, 255, 255, 0.1)'
                        )}
                    >
                        <option value={BY_NFT}>NFT</option>
                        <option value={BY_COLLECTION}>Collection</option>
                    </Select>
                </FormControl>
            </Flex>

            {isLoading && (
                <G3LogoSpinner
                    containerProps={{
                        h: '20vh',
                    }}
                />
            )}

            {(!groupBy || groupBy === BY_NFT) && (
                <Box marginTop="2rem">
                    {searchResults?.searchResults ? (
                        searchResults?.searchResults?.length > 0 ? (
                            !isLoading && (
                                <PageNFTGrid
                                    isCurrentUser={isCurrentUser}
                                    tokens={searchResults?.searchResults}
                                    hasNextPage={searchResults?.hasNextPage}
                                    loadNextPage={searchResults?.loadNextPage}
                                />
                            )
                        ) : (
                            <Text
                                padding="5rem 1rem"
                                textAlign="center"
                            >{`No results found for "${searchResults?.searchQueryText}"`}</Text>
                        )
                    ) : (
                        !isLoading && (
                            <PageNFTGrid
                                isCurrentUser={isCurrentUser}
                                tokens={
                                    genversePage?.tokens as GenverseTokenType[]
                                }
                                hasNextPage={hasNextPage}
                                loadNextPage={loadNextPage}
                            />
                        )
                    )}
                </Box>
            )}

            {!isLoading && (!groupBy || groupBy === BY_COLLECTION) && (
                <>
                    <Accordion
                        allowToggle
                        marginTop="2rem"
                        index={expandedIndex}
                        onChange={onChangeExpandedIndex}
                    >
                        {genversePage?.collections?.map(
                            (
                                { contract, tokens, totalOwned },
                                index: number
                            ) => {
                                const collectionName =
                                    contract?.name ||
                                    contract?.symbol ||
                                    tokens?.[0]?.metadata?.name ||
                                    '';
                                return (
                                    <AccordionItem borderColor="rgba(255, 255, 255, 0.1)">
                                        <AccordionButton>
                                            <Image
                                                height="2rem"
                                                width="2rem"
                                                objectFit="contain"
                                                objectPosition="center"
                                                marginRight="1rem"
                                                src={getTokenMetadataImage(
                                                    tokens?.[0] as GenverseTokenType
                                                )}
                                            />
                                            <Text
                                                fontSize="1rem"
                                                flex="1"
                                                textAlign="left"
                                                color={useColorModeValue(
                                                    'black',
                                                    'white'
                                                )}
                                            >
                                                {`${totalOwned} x ${collectionName}`}
                                            </Text>
                                            <AccordionIcon
                                                color={useColorModeValue(
                                                    'black',
                                                    'white'
                                                )}
                                            />
                                        </AccordionButton>
                                        <AccordionPanel padding="1rem 0 0 0">
                                            {expandedIndex === index && (
                                                <PageNFTGrid
                                                    isCurrentUser={
                                                        isCurrentUser
                                                    }
                                                    tokens={
                                                        tokens?.map(
                                                            (token) => ({
                                                                ...token,
                                                                contract,
                                                            })
                                                        ) as GenverseTokenType[]
                                                    }
                                                />
                                            )}
                                        </AccordionPanel>
                                    </AccordionItem>
                                );
                            }
                        )}
                    </Accordion>
                </>
            )}
        </>
    );
};
